import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IntegrationItemServiceInf, StatusString, IntegrationStatus } from '../interfaces/integrations';
import { FeatureToggleService } from 'src/app/core/services/feature-toggle.service';
import { TranslateService } from '@ngx-translate/core';
import { AppInjector } from '../../app-injector';
import { StatusType } from '../constants/statusType.enum';
import { GWPIntegrationService } from 'src/app/core/services/gwp-integration.service';
import { GatewayStatusService } from 'src/app/core/services/gateway-status.service';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { AdminService } from 'src/app/core/services/admin.service';
import { MiAccessService } from 'src/app/core/services/miaccess.service';
import { gatewayMessages, gatewayErrorMessages, commonMessages } from '../constants/constants';
import { Account } from '@mitel/cloudlink-sdk';
import { NotifierService } from 'angular-notifier';
import { ErrorsHandlerService } from 'src/app/core/services/errors-handler.service';
import { AccountsService } from './accounts.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { MenuItem } from '../interfaces/menu-item';
import { ProductLicensesService } from 'src/app/modules/users-single-edit/services/product-licenses.service';
import LRUCache from 'lru-cache';

@Injectable({
	providedIn: 'root'
})

// this service will hide whatever implementation we eventually have for getting the list of integrations
// types. Right now it is static
export class IntegGatewayService implements IntegrationItemServiceInf {

	private gwpIntegrationService: GWPIntegrationService;
	private gatewayStatusService: GatewayStatusService;
	private featureToggleService: FeatureToggleService;
	private adminService: AdminService;
	private accountsService: AccountsService;
	public dialog: MatDialog;
	private translateService: TranslateService;
	private miaccessService: MiAccessService;
	private notifierService: NotifierService;
	private errorHandlerService: ErrorsHandlerService;
	private rbacAuthGuardService: RBACAuthGuardService;
	private prodService: ProductLicensesService;
	private siteId: string;
	/**
	 * Cached by key = account ID.
	 */
	private lowLevelStatusCache = new LRUCache<string, Promise<any>>({
		ttl: 10 * 1000,  // ms (cache item expires in n sec) -- small ttl because we are only interested in sharing during concurrent access
		max: 10          // and, oldest item in cache is bumped if more than this items in cache
		// https://github.com/isaacs/node-lru-cache#readme
	}); // cache by account ID

	constructor(  ) 
	{
	}
	
	initializeServices()
	{
		if ( !this.gwpIntegrationService )
			this.gwpIntegrationService = AppInjector.injector.get(GWPIntegrationService);

		if ( !this.gatewayStatusService )
			this.gatewayStatusService = AppInjector.injector.get(GatewayStatusService);
		
		if ( !this.featureToggleService )
			this.featureToggleService = AppInjector.injector.get(FeatureToggleService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);
		
		if ( !this.translateService )
			this.translateService = AppInjector.injector.get(TranslateService);
		
		if ( !this.miaccessService )
			this.miaccessService = AppInjector.injector.get(MiAccessService);

		if ( !this.notifierService )
			this.notifierService = AppInjector.injector.get(NotifierService);

		if ( !this.errorHandlerService )
			this.errorHandlerService = AppInjector.injector.get(ErrorsHandlerService);
		
		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);
			
		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

		if (!this.prodService)
			this.prodService = AppInjector.injector.get(ProductLicensesService);
	}

	/*
	This HighLevelStatus is for the Account List page. 
	Although similar, it is not the same as the LowLevelStatus (for the Account Edit page).
	This HighLevelStatus should only be determined if the Gateway Integration is toggled ON
	*/
	getHighLevelStatus( account: Account, haltStatusUpdateSubject: Subject<boolean>, tags?): Promise<any> //Promise<IntegrationHighLevelStatus>
	{
		this.initializeServices();
		let detected = this.gwpIntegrationService.hasTunnelParameters();
		return this.gatewayStatusService.getHighLevelStatus(detected, account, haltStatusUpdateSubject, tags).then( statusDetails => {
			let integrationStatus: IntegrationStatus = {
				productCode: 'Gateway',
				integNameStatusDisplayString: 'Gateway',
				status: statusDetails? statusDetails.integrationHighLevelStatus: undefined
			};
			return Promise.resolve(integrationStatus);
		}).catch(err => {
			console.error("An error occurred while getting gateway integration status", err);
			return Promise.resolve(null);
		});
	}

	/*
	This LowLevelStatus is for the Gateway Integration that appears on the Account Edit page.
	Although similar, it is not the same as the HighLevelStatus (for the Account List page).
	This LowLevelStatus should only be determined if the Gateway Integration is toggled ON.
	We need to return the status here. We do need to call getLowLevelStatus so the status is 
	determined and stored in the lowLevelStatus class varibale of gatewayStatusService.
	Details of the stored status will then be retrieved with methods: getStatusString, getConfigButtonLabel, isButtonDisabled
	*/
	private _getLowLevelStatus( account: Account ): Promise<any>
	{
		this.initializeServices();
		let detected = this.gwpIntegrationService.hasTunnelParameters();
		return this.gatewayStatusService.getLowLevelStatus(detected, account).then( statusDetails => {
			return Promise.resolve(statusDetails);
		}).catch(err => {
			console.error("An error occurred while getting gateway integration status", err);
			return Promise.resolve(null);
		});
	}


	getLowLevelStatus(account: Account): Promise<any> {
		if (!this.lowLevelStatusCache.has(account.accountId)) {
			// In the cache is a promise, that may or may not be resolved.
			// If there are multiple callers to this method, (assuming same account) they will all get the same promise,
			// and when that promise resloves, they all get the same result.
			this.lowLevelStatusCache.set(account.accountId, this._getLowLevelStatus(account));
		}
		return this.lowLevelStatusCache.get(account.accountId);
	}

	canBeSelected( account: Account ) :  Promise<boolean>
	{
		// here we will get the service we need to use to check to see if this 
		// integration type is acceptable for the account
		// if accessFlag is false, we don't need to do checkPAOnboarding
		this.initializeServices();
		let accessFlag = this.gwpIntegrationService.hasGWPAccess();
		if(!accessFlag){
			return Promise.resolve(false);
		}
		return this.miaccessService.checkPAOnboarding(account).then( flag => {
			if(flag){
				let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONGATEWAY, ActionType.VIEW);
				flag = !isDenied;
			}
			return flag;
		})
		.catch( err => {
			console.error("Could not determine the Gateway access", err);
			return false;
		});
	}

	getConfigurationDialog() : any
	{
		return undefined;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		this.initializeServices();
		// For Gateway Portal, we want to show the setup button and the trash can for the integration-item.component when set up is not complete
		// if setup is complete, shows the gear icon and toggle button
		return this.getLowLevelStatus(account).then( statusDetails => {
			let completeMsg = this.translateService.instant(gatewayMessages.OVERVIEW);
			if(statusDetails && statusDetails.buttonMessage === completeMsg){
				return Promise.resolve(true);
			}
			else{
				return Promise.resolve(false);
			}	
		});	
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initializeServices();
		return Promise.resolve();
	}

	getLeftNavMenus( account: Account ): Promise<MenuItem[]> {
		this.initializeServices();
		//returning empty list as the menu is declared under Support and display if valid GWP policy
		//the method is defined so when a change happens in the GWP integration the menuService.checkForFeatureSpecificMenusForAccount is triggered 
		return Promise.resolve([]);
	}

	/*
	This method will return the status string to display on the Account Edit page for the Gateway Integration.
	The status was already determined when we called getLowLevelStatus. So we're just retrieving the stored status
	It is different than the string for the HighLevelStatus because sometimes it will show a little more details regarding the stepName
	Adding the stepName will make it match what users are used to seeing on the Partner Dash of Gateway Portal
	*/
	async getStatusString(account: Account): Promise<StatusString>
	{
		this.initializeServices();

		let status: StatusString = {
			status: undefined,
			type: StatusType.normal
		}
		const statusDetails = await this.getLowLevelStatus(account);

		// Currently the status types for the HighLevelStatus and the LowLevelStatus are not the same
		// So we need to convert
		if(statusDetails && statusDetails.integrationHighLevelStatus) {
			switch (statusDetails.integrationHighLevelStatus.state) {
				case 0: //UNKNOWN
					status.type = StatusType.error
					break;
				case 1: //GOOD
					status.type = StatusType.normal
					break;
				case 2: //INFO
					status.type = StatusType.caution
					break;
				case 3: //ERROR
					status.type = StatusType.error
					break;
			}	
		}
		if(statusDetails && statusDetails.statusMessage){
			status.status = statusDetails.statusMessage;
		}
		return Promise.resolve(status);
	}

	async getConfigButtonLabel(account: Account): Promise<string>
	{
		this.initializeServices();
		const status = await this.getLowLevelStatus(account);
		if(status && status.buttonMessage){
			return Promise.resolve(status.buttonMessage);
		} else {
			return Promise.resolve('');
		}
	}

	async isButtonDisabled(account: Account): Promise<boolean>
	{
		this.initializeServices();
		let status = await this.getLowLevelStatus(account);
		if(status && 'isButtonDisabled' in status){
			return Promise.resolve(status.isButtonDisabled);
		} else {
			return Promise.resolve(true);
		}
	}

	async canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initializeServices();
		//Better to get fresh tags here. They may have changed after the status was determined.
		const tags = await this.gatewayStatusService.getTagStatus(account.accountId);
		if (this.gwpIntegrationService.isGatewayConnected(tags)) 
		{	

			const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.DATA_RESET } });
			const contentComponentInstance = dialogRef.componentInstance;
			contentComponentInstance.customerName = account.name;
			contentComponentInstance.ipAddress = this.translateService.instant(commonMessages.LOADING);

			if ( !this.siteId )
			{
				this.siteId = await this.getSiteIdForAccount(account.accountId);
			}
			let networkConfig = await this.getNetworkSettings( account.accountId, this.siteId );

			if ( networkConfig )
			{
				let ipAddress;
				
				if (networkConfig.lan1.ipv4['current-address']) {
					ipAddress = networkConfig.lan1.ipv4['current-address'];
				} else if ((networkConfig.lan1.ipv4['address'])) {
					ipAddress = networkConfig.lan1.ipv4['address'];
				}

				contentComponentInstance.ipAddress = ipAddress;
			}
			else 
			{
				contentComponentInstance.ipAddress = this.translateService.instant(gatewayErrorMessages.FAILED_IP);
			}

			return dialogRef.afterClosed().toPromise().then(result => {
				if (result) {
					// If off, then update the tags
					this.dataResetGateway(account.accountId, this.siteId).then(reset => {
						if (reset) {
							return Promise.resolve(true);
						} else {
							let errMessage = gatewayErrorMessages.ERR_DATA_RESET;	
							this.notifierService.notify('error', this.translateService.instant(errMessage));
						}
					});
					return Promise.resolve(true);
				} else {
					return Promise.resolve(false);
				}
			});
		}
		else
		{
			return Promise.resolve(true);
		}
	}

	async dataResetGateway(accountId: string, siteId: string) {
		try {
			 await this.gwpIntegrationService.sendResetRequest(accountId, siteId);
			 return true;
		} catch (reason) {
			if (reason && reason.body) {
				if (reason.statusCode === 404) {
					console.error('failed to send data reset: has no connection to gateway');
				}
				if (reason.statusCode === 401) {
					// we are not going to logout if Tunnel API responds with 401.
					console.error('failed to send data reset: Tunnel API responded with 401');
				}
			} else {
				console.error('failed to send data reset', reason);
			}
			return false;
		}
	}

	async getSiteIdForAccount( accountId:string ): Promise<string>
	{
		try {
			const siteCollection = await this.adminService.getSites(accountId);
			if (siteCollection && siteCollection.count > 0 && siteCollection._embedded && siteCollection._embedded.items) {
				let sites = siteCollection._embedded.items;

				if (sites[0]) {
					return sites[0].siteId;
				}
			}
			return null;
		} catch (reason) {
			this.errorHandlerService.handleError(reason);
			if (reason && reason.body) {
				console.error('failed to get site', JSON.stringify(reason.body));
			} else {
				console.error('failed to get site', reason);
			}
			return null;
		}
	}

	async getNetworkSettings( accountId: string, siteId: string ): Promise<any>
	{
		try {
			const response = await this.gwpIntegrationService.getNetworkSettings(accountId, siteId);
			return response.result;
		} catch (reason) {
				if (typeof reason === 'string') {
						console.error('failed to get network settings', reason);
				} else if (reason && reason.statusCode === 401) {
						console.log('failed to get network settings', JSON.stringify(reason));
				} else if (reason instanceof Error) {
						console.error('failed to get network settings', reason.message);
				} else {
					console.log('failed to get network settings', JSON.stringify(reason));
				}
				return null;
		}
	}

	/* Only auto add the gwp integration if someone has previously attempted to onboard.
	E.g Someone may have started onboarding in the standalone gwp.
	If no signs of past onboarding, don't auto add the integration.
	*/
	addIntegrationByDefault(tags?: any): Promise<boolean> {
		if(tags && tags['on-board-progress'])
		{
			return Promise.resolve(true);
		}
		else{
			return Promise.resolve(false);
		}
	}

	getInfoLink(account: Account): string
	{
		return "integrations.mitel-help.gateway";
	}

	// For now, only the mivb product is associated with the gwp integration
	getUserProducts(): any {
		this.initializeServices();

		return this.prodService.getGWPUserProduct('mivb');
	}
}